/**
 * RPS-RISK
 * Risques psychosociaux d'une fonction (Population.rpsRisks) : des situations de travail par axe, et un niveau,
 * sur une echelle RPS fixe de quatre niveaux, independante des reglages PHY.
 */

import getColor from "@/utils/get-color.utils"

export const RPS_RISK_AXES = [
  { id: "work-intensity", label: "Intensité et complexité du travail" },
  { id: "difficult-hours", label: "Horaires de travail difficiles" },
  { id: "emotional-demands", label: "Exigences émotionnelles" },
  { id: "autonomy", label: "Autonomie dans le travail" },
  { id: "social-relations", label: "Rapports sociaux au travail" },
  { id: "value-conflicts", label: "Conflits de valeurs" },
  { id: "job-insecurity", label: "Insécurité de l'emploi et du travail" }
] as const

export type RpsRiskAxis = (typeof RPS_RISK_AXES)[number]["id"]

export interface RpsRisk {
  axis: RpsRiskAxis
  situations: string[]
  level: number | null //Non defini, ou index RPS de 0 (le moins expose) a 3 (le plus expose)
}

export function getRpsRisk(rpsRisks: RpsRisk[] = [], axis: RpsRiskAxis): RpsRisk | undefined {
  return rpsRisks.find((rpsRisk) => rpsRisk.axis === axis)
}

//Case ou pastille d'un niveau non renseigne
export const RPS_EMPTY_COLOR = "#EEF0F3"

export const RPS_RISK_LEVELS = [
  { label: "Faible", color: getColor("green") },
  { label: "Modéré", color: getColor("yellow") },
  { label: "Moyen", color: getColor("orange") },
  { label: "Élevé", color: getColor("red") }
] as const

const RPS_AXIS_LEVEL_LABELS: Record<RpsRiskAxis, readonly string[]> = {
  "work-intensity": ["Faible", "Modérée", "Forte", "Très forte"],
  "emotional-demands": ["Faibles", "Modérées", "Fortes", "Très fortes"],
  autonomy: ["Forte", "Moyenne", "Faible", "Aucune"],
  "social-relations": ["Bons", "Corrects", "Tendus", "Dégradés"],
  "value-conflicts": ["Rares", "Occasionnels", "Fréquents", "Très fréquents"],
  "difficult-hours": ["Adaptés", "Peu contraignants", "Contraignants", "Très contraignants"],
  "job-insecurity": ["Faible", "Modérée", "Forte", "Très forte"]
}

export function getRpsRiskLevels(axis?: RpsRiskAxis) {
  return RPS_RISK_LEVELS.map((level, index) => ({
    ...level,
    label: axis ? RPS_AXIS_LEVEL_LABELS[axis][index] : level.label
  }))
}

export function getRpsLevel(level: number | null, axis?: RpsRiskAxis) {
  return level === null ? null : getRpsRiskLevels(axis)[Math.max(0, Math.min(level, RPS_RISK_LEVELS.length - 1))] ?? null
}

//Conserver un axe des qu'un niveau ou une situation est renseigne. Les situations vides sont retirees.
export function setRpsRisk(rpsRisks: RpsRisk[], rpsRisk: RpsRisk): RpsRisk[] {
  const others = rpsRisks.filter((item) => item.axis !== rpsRisk.axis)
  const situations = rpsRisk.situations.map((situation) => situation.trim()).filter(Boolean)
  const level = rpsRisk.level === null ? null : Math.max(0, Math.min(rpsRisk.level, RPS_RISK_LEVELS.length - 1))
  return situations.length || level !== null ? [...others, { ...rpsRisk, situations, level }] : others
}

